import { 
  Node, Transaction, Goal, DirectoryMember, NotificationPreference, 
  RequirementGroup, MemberRequirementSubmission, Newsletter, 
  MemberRelationship, NodeConfig, Message, PulseAlert, VaultItem, 
  OrgEvent, Issue, PreferencesResponse,
  MessageBoardTopic, MessageBoardComment,
  Election, Candidate, Referral,
  PulseType, 
  PulseSeverity, 
  IssueStatus, 
  IssueCategory, 
  IssueSeverity, 
  Contribution, 
  ContributionStatus 
} from '../types';
import { getBrandedNode, MOCK_NODE as BRANDED_BASE } from './branding';
import preloadedArticles from './preloaded-articles.json';

export const MOCK_CONTRIBUTIONS: Contribution[] = [
  {
    id: 'c1',
    nodeId: 'node-1',
    authorId: 'user-james',
    authorName: 'James Avery',
    title: 'Spring Picnic',
    content: 'Took these photos at the Spring Chapter picnic. Everyone had a blast!',
    imageUrl: 'https://picsum.photos/seed/picnic/800/600',
    status: ContributionStatus.PENDING,
    createdAt: new Date().toISOString()
  },
  {
    id: 'c2',
    nodeId: 'node-1',
    authorId: 'user-mike',
    authorName: 'Multi-Org Mike',
    title: 'New Research',
    content: 'Working on a new research paper for the National archives. This could be a great spotlight for the next issue.',
    status: ContributionStatus.APPROVED,
    createdAt: new Date().toISOString()
  },
  {
    id: 'c3',
    nodeId: 'node-1',
    authorId: 'user-james',
    authorName: 'James Avery',
    title: 'Studio Setup',
    content: 'Check out the new streaming setup in the local hub!',
    imageUrl: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&auto=format&fit=crop&q=60',
    status: ContributionStatus.PENDING,
    createdAt: new Date(Date.now() - 86400000).toISOString()
  }
];

export const MOCK_STATE_KEYS = {
  USER_ROLE: 'mock_user_role',
  ACTIVE_ORG: 'mock_active_org',
  THEME: 'mock_theme',
  USER_IDENTITY: 'mock_user_identity'
};

export const MOCK_USERS = [
  { id: 'user-james', name: 'James Avery', email: 'james@orgsets.com', avatar: 'https://picsum.photos/seed/james/100/100' },
  { id: 'user-alice', name: 'Admin Alice', email: 'alice@admin.com', avatar: 'https://picsum.photos/seed/alice/100/100' },
  { id: 'user-mike', name: 'Multi-Org Mike', email: 'mike@member.com', avatar: 'https://picsum.photos/seed/mike/100/100' },
  { id: 'user-new', name: 'Guest User', email: 'guest@new.com', avatar: 'https://picsum.photos/seed/guest/100/100' },
  { id: 'user-system', name: 'System Admin', email: 'sysadmin@orgsets.com', avatar: 'https://picsum.photos/seed/sysadmin/100/100' },
];

export const MOCK_MEMBERSHIPS_MAP: Record<string, any[]> = {
  'user-james': [
    { id: 'node-1', nodeName: 'Local Chapter', nodeSlug: 'demo-org', role: 'MEMBER', unreadCount: 0, parentNodeId: 'node-2', tier: 'MOMENTUM', memberCount: 154 },
    { id: 'node-2', nodeName: 'Grand Council', nodeSlug: 'council', role: 'MEMBER', unreadCount: 0, tier: 'INSTITUTIONAL', memberCount: 2840 },
    { id: 'node-3', nodeName: 'StellarHub HQ', nodeSlug: 'stellarhub', role: 'MISSION_COMMANDER', unreadCount: 0, tier: 'INSTITUTIONAL', memberCount: 88 }
  ],
  'user-alice': [
    { id: 'node-1', nodeName: 'Local Chapter', nodeSlug: 'demo-org', role: 'PRESIDENT', unreadCount: 0, parentNodeId: 'node-2', tier: 'MOMENTUM', memberCount: 154 },
    { id: 'node-2', nodeName: 'Grand Council', nodeSlug: 'council', role: 'ADMIN', unreadCount: 0, tier: 'INSTITUTIONAL', memberCount: 2840 }
  ],
  'user-mike': [
    { id: 'node-1', nodeName: 'Local Chapter', nodeSlug: 'demo-org', role: 'MEMBER', unreadCount: 0, parentNodeId: 'node-2', tier: 'MOMENTUM' },
    { id: 'node-2', nodeName: 'Grand Council', nodeSlug: 'council', role: 'ADMIN', unreadCount: 0, tier: 'INSTITUTIONAL' }
  ],
  'user-new': [
    { id: 'node-4', nodeName: 'Indie Foundation', nodeSlug: 'indie', role: 'MEMBER', unreadCount: 0, tier: 'LOCAL' }
  ],
  'user-system': [
    { id: 'node-1', nodeName: 'Local Chapter', nodeSlug: 'demo-org', role: 'SYSTEM_ADMIN', unreadCount: 0, parentNodeId: 'node-2', tier: 'MOMENTUM', memberCount: 154 },
    { id: 'node-2', nodeName: 'Grand Council', nodeSlug: 'council', role: 'SYSTEM_ADMIN', unreadCount: 0, tier: 'INSTITUTIONAL', memberCount: 2840 },
    { id: 'node-3', nodeName: 'StellarHub HQ', nodeSlug: 'stellarhub', role: 'SYSTEM_ADMIN', unreadCount: 0, tier: 'INSTITUTIONAL', memberCount: 88 }
  ]
};

export { getBrandedNode };

export const MOCK_NODE: Node = BRANDED_BASE;

export const NODES: Node[] = [
  MOCK_NODE,
  {
    ...MOCK_NODE,
    id: 'node-2',
    name: 'Grand Council',
    slug: 'council',
    lineageIds: [],
    tier: 'INSTITUTIONAL',
    visibility: 'PROTECTED',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    config: {
      ...MOCK_NODE.config,
      primaryColor: '#1e3a8a', // Dark blue
      secondaryColor: '#3b82f6',
      terminology: {
        ...MOCK_NODE.config.terminology,
        member: 'Legionnaire', // Grand Council calls members Legionnaires
        childNode: 'Outpost',
        parentNode: 'Empire'
      },
      roleDefinitions: [
        {
          name: 'CAPTAIN',
          capabilities: ['create_announcement', 'view_chat', 'view_ledger'],
          isLocked: false,
          inherited: true
        },
        {
          name: 'COORDINATOR',
          capabilities: ['view_directory', 'create_event'],
          isLocked: false,
          inherited: true
        }
      ]
    }
  },
  {
    ...MOCK_NODE,
    id: 'node-5',
    name: 'Graffiti HUB',
    slug: 'street-crews',
    visibility: 'PUBLIC',
    config: {
      ...MOCK_NODE.config,
      primaryColor: '#FF00FF',
      secondaryColor: '#00FFFF',
      themeId: 'bauhaus',
      marketingCopy: {
        heroHeadline: 'Paint the Town.',
        heroSubheadline: 'A High-Energy Workspace for Creative Collectives.',
        heroCta: 'Join the Movement',
        featuresHeadline: 'Loud. Proud. Organized.',
        featuresSubheadline: 'Graffiti HUB is built for the crews that value style as much as structure.'
      }
    }
  },
  {
    ...MOCK_NODE,
    id: 'node-6',
    name: 'Atomic Society',
    slug: 'atomicsociety',
    visibility: 'PUBLIC',
    config: {
      ...MOCK_NODE.config,
      primaryColor: '#00FF41',
      secondaryColor: '#1A1A1A',
      themeId: 'bauhaus',
      marketingCopy: {
        heroHeadline: 'Rebuild Together.',
        heroSubheadline: 'Secure coordination for your wasteland crew.',
        heroCta: 'Create Free Organization',
        featuresHeadline: 'Atomic-Age Reliability.',
        featuresSubheadline: 'Hardened against communication failure.'
      }
    }
  }
];

export const MOCK_ANNOUNCEMENTS: PulseAlert[] = [
  {
    id: 'ann-1',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    title: 'Welcome to OrgSets!',
    authorUserId: 'user-1',
    authorName: 'James Avery',
    authorRole: 'PRESIDENT',
    body: 'We are excited to have you here. Explore the dashboard to see what is new.',
    type: PulseType.GENERAL,
    severity: PulseSeverity.MEDIUM,
    audience: 'MEMBERS',
    createdAt: new Date().toISOString(),
    acknowledgedBy: ['user-2'],
    relayCount: 5,
    targetRoles: ['All roles'],
    cascadesDown: true,
    requiresAck: true
  },
  {
    id: 'ann-public-1',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    title: 'Open House This Saturday',
    authorUserId: 'user-1',
    authorName: 'James Avery',
    authorRole: 'PRESIDENT',
    body: 'We are opening our doors to the public this Saturday at 10 AM. Join us for a tour and meet the team!',
    type: PulseType.GENERAL,
    severity: PulseSeverity.LOW,
    audience: 'PUBLIC',
    createdAt: new Date(Date.now() - 43200000).toISOString(), // 12h ago
    acknowledgedBy: [],
    relayCount: 120,
    targetRoles: ['All roles'],
    cascadesDown: false,
    requiresAck: false
  },
  {
    id: 'ann-3',
    nodeId: 'node-2',
    nodeName: 'Grand Council',
    title: 'Updated Policy: Fee Distribution',
    authorUserId: 'user-admin-parent',
    authorName: 'Chief Commissioner',
    authorRole: 'NATIONAL_ADMIN',
    body: 'Please review the updated policy on how fees will be distributed across the hierarchy.',
    type: PulseType.GOVERNANCE,
    severity: PulseSeverity.HIGH,
    audience: 'MEMBERS',
    createdAt: new Date(Date.now() - 3600000).toISOString(), // 1h ago
    acknowledgedBy: [],
    relayCount: 150,
    targetRoles: ['All roles'],
    cascadesDown: true,
    requiresAck: false
  },
  {
    id: 'ann-2',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    title: 'April Newsletter Now Available',
    authorUserId: 'user-2',
    authorName: 'Jane Smith',
    authorRole: 'SECRETARY',
    body: 'The April newsletter is now available in the Vault. Check it out for deep dives into our new strategy and upcoming community events.',
    type: PulseType.GENERAL,
    severity: PulseSeverity.LOW,
    audience: 'MEMBERS',
    createdAt: new Date(Date.now() - 86400000).toISOString(),
    acknowledgedBy: [],
    relayCount: 12,
    targetRoles: ['All roles'],
    cascadesDown: false,
    requiresAck: false
  }
];

export const MOCK_TRANSACTIONS: Transaction[] = [
  {
    id: 'tx-1',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    userId: 'user-1',
    userName: 'James Avery',
    amount: 50.00,
    description: 'Monthly Membership Dues',
    status: 'PAID',
    createdAt: new Date().toISOString()
  },
  {
    id: 'tx-dep-1',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    userId: 'user-child-1',
    userName: 'Timmy Avery',
    amount: 25.00,
    description: 'Quarterly Team Fee (Paid by James Avery)',
    status: 'PAID',
    createdAt: new Date(Date.now() - 86400000).toISOString()
  },
  {
    id: 'tx-3',
    nodeId: 'node-7',
    nodeName: 'East Side Chapter',
    userId: 'user-chapter-1',
    userName: 'Officer Friendly',
    amount: 15.00,
    description: 'Local Chapter Assessment',
    status: 'PAID',
    createdAt: new Date(Date.now() - 7200000).toISOString()
  },
  {
    id: 'tx-2',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    userId: 'user-2',
    userName: 'Jane Smith',
    amount: 25.00,
    description: 'Event Registration Fee',
    status: 'PENDING',
    createdAt: new Date(Date.now() - 3600000).toISOString()
  }
];

export const MOCK_GOALS: Goal[] = [
  {
    id: 'goal-1',
    nodeId: 'node-1',
    title: 'Annual Fundraising',
    description: 'Raise funds for community projects.',
    targetValue: 50000,
    currentValue: 32500,
    unit: '$',
    deadline: '2026-12-31',
    isPublic: true,
    subGoals: [
      {
        id: 'goal-1-1',
        nodeId: 'node-1',
        title: 'Spring Gala',
        description: 'Funds from the spring event.',
        targetValue: 15000,
        currentValue: 12000,
        unit: '$',
        deadline: '2026-05-15',
        isPublic: true,
        subGoals: []
      }
    ]
  }
];

export const MOCK_VAULT_ITEMS: VaultItem[] = [
  {
    id: 'vault-1',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    title: 'Bylaws 2024',
    description: 'The latest version of our organizational bylaws.',
    fileUrl: 'https://example.com/bylaws.pdf',
    fileName: 'bylaws_2024.pdf',
    fileSize: 1024 * 1024 * 2.5,
    fileType: 'application/pdf',
    visibility: 'PRIVATE',
    uploadedBy: 'user-1',
    uploadedByName: 'John Doe',
    createdAt: '2024-01-15T10:00:00Z'
  },
  {
    id: 'vault-2',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    title: 'Training Manual',
    description: 'Required reading for all members in the organization branch.',
    fileUrl: 'https://example.com/manual.pdf',
    fileName: 'training_manual.pdf',
    fileSize: 1024 * 1024 * 15,
    fileType: 'application/pdf',
    visibility: 'SHARED_DOWN',
    uploadedBy: 'user-2',
    uploadedByName: 'Jane Smith',
    createdAt: '2024-02-20T14:30:00Z'
  },
  {
    id: 'vault-3',
    nodeId: 'node-child',
    nodeName: 'West Coast Chapter',
    title: 'Local Event Permits',
    description: 'Permits for the upcoming regional conference.',
    fileUrl: 'https://example.com/permits.zip',
    fileName: 'conference_permits.zip',
    fileSize: 1024 * 1024 * 45,
    fileType: 'application/zip',
    visibility: 'SHARED_UP',
    uploadedBy: 'user-3',
    uploadedByName: 'Bob Wilson',
    createdAt: '2024-03-05T09:15:00Z'
  }
];

export const MOCK_NEWSLETTERS: Newsletter[] = [
  {
    id: 'news-1',
    nodeId: 'node-1',
    authorUserId: 'user-1',
    authorName: 'John Doe',
    title: 'Spring 2024 Newsletter',
    subjectLine: 'Whats blooming at Demo Org this spring?',
    previewText: 'Check out our upcoming events and chapter updates.',
    status: 'PUBLISHED',
    scheduledFor: null,
    publishedAt: '2024-03-20T08:00:00Z',
    thumbnailUrl: 'https://picsum.photos/seed/newsletter1/400/600',
    cascadesDown: true,
    sections: [
      {
        id: 'sec-1',
        type: 'hero',
        orderIndex: 0,
        title: 'Spring is Here!',
        subtitle: 'Growth and Renewal in our Chapter',
        imageUrl: 'https://images.unsplash.com/photo-1490750967814-387063467615?w=800&auto=format&fit=crop',
        ctaText: 'Visit Chapter Hub',
        ctaUrl: '/dashboard'
      },
      {
        id: 'sec-2',
        type: 'text',
        orderIndex: 1,
        htmlContent: '<p>Welcome to the spring edition of our organizational newsletter. We have many exciting events planned for the coming months. From murial sessions to community mixers, there is something for everyone.</p>'
      },
      {
        id: 'sec-3',
        type: 'notice',
        orderIndex: 2,
        title: 'Membership Fees Due',
        content: 'Please remember to settle your quarterly dues by the end of this month to ensure continued access to chapter resources.',
        severity: 'info'
      }
    ],
    metrics: {
      recipientsTotal: 154,
      deliveredEmail: 150,
      deliveredSms: 40,
      deliveredPush: 80,
      uniqueOpens: 95,
      linkClicks: 24,
      bounces: 4
    },
    createdAt: '2024-03-10T10:00:00Z',
    updatedAt: '2024-03-20T08:00:00Z',
    version: 5
  },
  {
    id: 'news-2',
    nodeId: 'node-2',
    authorUserId: 'user-hq',
    authorName: 'Admin Alpha',
    title: 'HQ Monthly Digest - February',
    subjectLine: 'National Strategy Update',
    previewText: 'A word from the Grand Council on our vision for 2026.',
    status: 'PUBLISHED',
    scheduledFor: null,
    publishedAt: '2024-02-15T12:00:00Z',
    thumbnailUrl: 'https://picsum.photos/seed/newsletter2/400/600',
    cascadesDown: true,
    sections: [
      {
        id: 'sec-4',
        type: 'text',
        orderIndex: 0,
        htmlContent: '<h1>National Strategy Update</h1><p>The Grand Council is pleased to announce the roadmap for our next growth phase...</p>'
      }
    ],
    metrics: {
      recipientsTotal: 1240,
      deliveredEmail: 1200,
      deliveredSms: 0,
      deliveredPush: 500,
      uniqueOpens: 800,
      linkClicks: 350,
      bounces: 40
    },
    createdAt: '2024-02-01T09:00:00Z',
    updatedAt: '2024-02-15T12:00:00Z',
    version: 3
  }
];

export const MOCK_TOPICS: MessageBoardTopic[] = [
  {
    id: 'topic-1',
    nodeId: 'node-1',
    nodeName: 'Graffiti HUB',
    title: 'Welcome to our new Discussion Board!',
    content: 'This is the place to share ideas, ask questions, and collaborate in the open. We wanted a home that reflected our energy and kept us organized. What features should we add next?\n\nI personally want a way to tag locations on a collaborative map!',
    authorUserId: 'user-james',
    authorName: 'James Avery',
    authorAvatarColor: 'bg-brand-primary',
    isPrivate: false,
    cascadesDown: true,
    commentCount: 4,
    createdAt: new Date(Date.now() - 3600000 * 24).toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'topic-2',
    nodeId: 'node-1',
    nodeName: 'Graffiti HUB',
    title: 'Annual Creative Retreat 2026',
    content: 'Gathering thoughts on locations for our next retreat. Should we stick to the coast or go deep into the mountains this year? Looking for somewhere with great light and plenty of space for collaborative workshops.',
    authorUserId: 'user-alice',
    authorName: 'Admin Alice',
    authorAvatarColor: 'bg-teal-500',
    isPrivate: false,
    cascadesDown: false,
    commentCount: 2,
    createdAt: new Date(Date.now() - 3600000 * 48).toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'topic-3',
    nodeId: 'node-2',
    nodeName: 'Grand Council HQ',
    title: '[Cascaded] National Strategy for 2026',
    content: 'We are seeking feedback from all regional chapters on our upcoming strategy. Please discuss the implications for your local nodes below. This is about making our collective impact even stronger while keeping the local soul alive.',
    authorUserId: 'user-hq',
    authorName: 'Admin Alpha',
    authorAvatarColor: 'bg-blue-600',
    isPrivate: false,
    cascadesDown: true,
    commentCount: 1,
    createdAt: new Date(Date.now() - 3600000 * 72).toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'topic-4',
    nodeId: 'node-1',
    nodeName: 'Graffiti HUB',
    title: 'Budget Review: Community Project Funds',
    content: 'Opening the floor for the review of last quarter\'s spending. Transparency is core to how we work. Please read the attached ledger report in the Vault before commenting.',
    authorUserId: 'user-james',
    authorName: 'James A.',
    authorAvatarColor: 'bg-brand-primary',
    isPrivate: false,
    cascadesDown: false,
    commentCount: 0,
    createdAt: new Date(Date.now() - 3600000 * 5).toISOString(),
    updatedAt: new Date().toISOString(),
  }
];

export const MOCK_COMMENTS: Record<string, MessageBoardComment[]> = {
  'topic-1': [
    {
      id: 'c-1',
      topicId: 'topic-1',
      content: 'I love the nested structure! It makes long conversations so much easier to follow. It feels like a real garden of ideas.',
      authorUserId: 'user-alice',
      authorName: 'Admin Alice',
      authorAvatarColor: 'bg-teal-500',
      depth: 0,
      createdAt: new Date(Date.now() - 3600000 * 5).toISOString(),
    },
    {
      id: 'c-1-1',
      topicId: 'topic-1',
      parentId: 'c-1',
      content: 'Exactly! This is what we were missing in the previous chat-only setup. No more scrolling forever to find a specific thought.',
      authorUserId: 'user-mike',
      authorName: 'Multi-Org Mike',
      authorAvatarColor: 'bg-indigo-500',
      depth: 1,
      createdAt: new Date(Date.now() - 3600000 * 4).toISOString(),
    },
    {
      id: 'c-1-1-1',
      topicId: 'topic-1',
      parentId: 'c-1-1',
      content: 'Can we also get rich text support in comments? Being able to link directly to inspiration would be huge.',
      authorUserId: 'user-james',
      authorName: 'James Avery',
      authorAvatarColor: 'bg-brand-primary',
      depth: 2,
      createdAt: new Date(Date.now() - 3600000 * 3).toISOString(),
    },
    {
      id: 'c-2',
      topicId: 'topic-1',
      content: 'Will these boards be visible to potential members or just our inner circle?',
      authorUserId: 'user-new',
      authorName: 'Guest User',
      authorAvatarColor: 'bg-amber-500',
      depth: 0,
      createdAt: new Date(Date.now() - 3600000 * 2).toISOString(),
    }
  ],
  'topic-2': [
    {
      id: 'c-t2-1',
      topicId: 'topic-2',
      content: 'Mountains all the way! The fresh air and quiet would be perfect for our design sprints.',
      authorUserId: 'user-james',
      authorName: 'James Avery',
      authorAvatarColor: 'bg-brand-primary',
      depth: 0,
      createdAt: new Date(Date.now() - 3600000 * 10).toISOString(),
    },
    {
      id: 'c-t2-2',
      topicId: 'topic-2',
      content: 'I agree, the coast is beautiful but the mountains feel more "retreat-like" for 2026.',
      authorUserId: 'user-mike',
      authorName: 'Multi-Org Mike',
      authorAvatarColor: 'bg-indigo-500',
      depth: 0,
      createdAt: new Date(Date.now() - 3600000 * 8).toISOString(),
    }
  ],
  'topic-3': [
    {
      id: 'c-t3-1',
      topicId: 'topic-3',
      content: 'The scale seems ambitious, but I think with the new cascading pulse feeds, we can keep everyone in the loop without overhead.',
      authorUserId: 'user-james',
      authorName: 'James Avery',
      authorAvatarColor: 'bg-brand-primary',
      depth: 0,
      createdAt: new Date(Date.now() - 3600000).toISOString(),
    }
  ],
  'topic-4': []
};

export const MOCK_DIRECTORY: DirectoryMember[] = [
  {
    id: 'mem-1',
    userId: 'user-1',
    name: 'James Avery',
    avatarUrl: 'https://picsum.photos/seed/admin/100/100',
    email: 'james@avery.com',
    role: 'PRESIDENT',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    joinedAt: '2024-01-01T00:00:00Z',
    skills: ['Leadership', 'Strategy', 'Finance'],
    metadata: { phone: '555-0101', department: 'Executive', status: 'ACTIVE', privacy: 'PRIVATE' },
    memberType: 'SPONSOR',
    
  },
  {
    id: 'mem-2',
    userId: 'user-2',
    name: 'John Doe',
    avatarUrl: 'https://picsum.photos/seed/john/100/100',
    email: 'john.doe@example.com',
    role: 'MEMBER',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    joinedAt: '2024-02-15T00:00:00Z',
    skills: ['Design', 'Marketing'],
    metadata: { phone: '555-0102', department: 'Creative', status: 'ACTIVE' },
    memberType: 'PRIMARY'
  },
  {
    id: 'mem-3',
    userId: 'user-child-1',
    name: 'Timmy Avery',
    avatarUrl: 'https://picsum.photos/seed/timmy/100/100',
    email: 'timmy@avery.com',
    role: 'PLAYER',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    joinedAt: '2024-03-01T00:00:00Z',
    skills: ['Soccer'],
    metadata: { grade: '2nd', status: 'ACTIVE' },
    memberType: 'DEPENDENT',
    
  },
  {
    id: 'mem-4',
    userId: 'user-alice',
    name: 'Admin Alice',
    avatarUrl: 'https://picsum.photos/seed/alice/100/100',
    email: 'alice@admin.com',
    role: 'ADMIN',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    joinedAt: '2024-01-05T00:00:00Z',
    skills: ['Administration', 'Governance'],
    metadata: { department: 'Operations', status: 'ACTIVE' },
    memberType: 'PRIMARY'
  },
  {
    id: 'mem-5',
    userId: 'user-mike',
    name: 'Multi-Org Mike',
    avatarUrl: 'https://picsum.photos/seed/mike/100/100',
    email: 'mike@multiorg.com',
    role: 'MODERATOR',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    joinedAt: '2024-03-10T00:00:00Z',
    skills: ['Moderation', 'Community'],
    metadata: { department: 'Communication', status: 'ACTIVE' },
    memberType: 'PRIMARY'
  },
  {
    id: 'mem-pending-1',
    userId: 'user-new-pending',
    name: 'Sarah Jenkins',
    avatarUrl: 'https://picsum.photos/seed/sarah/100/100',
    email: 'sarah.j@example.com',
    role: 'MEMBER',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    joinedAt: new Date().toISOString(),
    skills: ['Photography'],
    metadata: { department: 'Waiting List', status: 'PENDING' },
    memberType: 'PRIMARY'
  },
  {
    id: 'mem-6',
    userId: 'user-sarah',
    name: 'Sarah Smith',
    email: 'sarah.smith@example.com',
    role: 'TREASURER',
    nodeId: 'node-2',
    nodeName: 'Western Division',
    joinedAt: '2024-04-01T00:00:00Z',
    skills: ['Accounting', 'Audit'],
    metadata: { status: 'ACTIVE' },
    memberType: 'PRIMARY'
  },
  {
    id: 'mem-7',
    userId: 'user-bob',
    name: 'Bob Builder',
    email: 'bob@build.com',
    role: 'MEMBER',
    nodeId: 'node-3',
    nodeName: 'Santa Monica Chapter',
    joinedAt: '2024-04-15T00:00:00Z',
    skills: ['Maintenance', 'Events'],
    metadata: { status: 'ACTIVE' },
    memberType: 'PRIMARY'
  }
];

export const MOCK_RELATIONSHIPS: MemberRelationship[] = [
  {
    id: 'rel-1',
    nodeId: 'node-1',
    dependentId: 'mem-3',
    sponsorId: 'mem-1',
    relationshipType: 'PARENT',
    isFinancialSponsor: true,
    isCommunicationSponsor: true
  }
];

export const MOCK_NOTIFICATIONS: NotificationPreference[] = [
  { type: 'URGENT', label: 'Urgent Announcements', app: true, email: false, sms: true, push: true, locked: ['app', 'sms'] },
  { type: 'REGULAR', label: 'Regular Announcements', app: true, email: true, sms: false, push: true, locked: [] },
  { type: 'CHAT', label: 'New Chat Message', app: true, email: false, sms: false, push: true, locked: [] }
];

export const MOCK_USER_PROFILE = {
  id: 'user_mock_admin',
  email: 'admin@orgsets.com',
  firstName: 'Admin',
  lastName: 'User',
  imageUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150',
  globalRole: 'SUPER_ADMIN',
  displayName: 'Admin User',
  avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150',
  bio: 'Platform administrator',
  availabilityStatus: 'ONLINE',
  hideMobile: true,
  hideEmail: false,
  preferences: {
    connectedApps: {
      facebook: false,
      twitter: false
    }
  },
  connectedApps: {
    facebook: false,
    twitter: false
  },
  memberships: [],
  relationships: [],
  flags: {
    isActive: true,
    availabilityStatus: 'ONLINE',
    hideMobile: true,
    hideEmail: false
  }
};

export const MOCK_USER_PREFERENCES: PreferencesResponse = {
  global: MOCK_NOTIFICATIONS,
  nodeSpecific: {
    'node-1': [
      { type: 'URGENT', label: 'Urgent Announcements', app: true, email: true, sms: false, push: true, locked: ['app'] }
    ]
  }
};

export const MOCK_REQUIREMENT_GROUPS: RequirementGroup[] = [
  {
    id: 'rg-parent-1',
    nodeId: 'node-2',
    nodeName: 'Grand Council',
    name: 'National Compliance',
    description: 'Mandatory items for all national members.',
    requirements: [
      { id: 'r1', nodeId: 'node-2', title: 'National Code of Conduct Agreement', description: '', status: 'DONE', groupId: 'rg-parent-1', groupName: 'National Compliance' },
      { id: 'r2', nodeId: 'node-2', title: 'Safe Environment Training', description: '', status: 'PENDING', groupId: 'rg-parent-1', groupName: 'National Compliance' }
    ],
    progress: { total: 2, completed: 1, percentage: 50 }
  },
  {
    id: 'rg-local-1',
    nodeId: 'node-1',
    nodeName: 'Demo Organization',
    name: 'Local Chapter Onboarding',
    description: 'Welcome items for our local community.',
    requirements: [
      { id: 'r3', nodeId: 'node-1', title: 'Local Group Orientation', description: '', status: 'DONE', groupId: 'rg-local-1', groupName: 'Local Chapter Onboarding' },
      { id: 'r4', nodeId: 'node-1', title: 'Introduction to Local Facilities', description: '', status: 'TODO', groupId: 'rg-local-1', groupName: 'Local Chapter Onboarding' }
    ],
    progress: { total: 2, completed: 1, percentage: 50 }
  }
];

export const MOCK_ANALYTICS = {
  memberGrowth: [
    { name: 'Jan', count: 2100 },
    { name: 'Feb', count: 2250 },
    { name: 'Mar', count: 2400 },
    { name: 'Apr', count: 2847 },
  ],
  revenueByMonth: [
    { name: 'Jan', amount: 8500 },
    { name: 'Feb', amount: 10200 },
    { name: 'Mar', amount: 12100 },
    { name: 'Apr', amount: 14250 },
  ],
  announcementEngagement: [
    { name: 'Welcome', reads: 85, ack: 70 },
    { name: 'Policy Update', reads: 42, ack: 0 },
    { name: 'Spring Gala', reads: 120, ack: 45 },
    { name: 'Newsletter', reads: 65, ack: 0 },
  ],
  memberRoles: [
    { name: 'President', value: 1 },
    { name: 'Treasurer', value: 1 },
    { name: 'Coach', value: 12 },
    { name: 'Moderator', value: 5 },
    { name: 'Member', value: 2828 },
  ]
};

export const MOCK_ANALYTICS_SUMMARY = {
  totalMembers: 2847,
  memberGrowthPercent: 12.5,
  totalRevenue: 45050,
  revenueGrowthPercent: 8.2,
  engagementRate: 68.4,
  activePulsePosts: 12,
  upcomingEventsCount: 8
};

export const MOCK_ANALYTICS_TIMESERIES = {
  members: [
    { date: '2024-04-01', value: 2700 },
    { date: '2024-04-05', value: 2720 },
    { date: '2024-04-10', value: 2750 },
    { date: '2024-04-15', value: 2780 },
    { date: '2024-04-20', value: 2810 },
    { date: '2024-04-25', value: 2830 },
    { date: '2024-04-30', value: 2847 },
  ],
  revenue: [
    { date: '2024-04-01', value: 12000 },
    { date: '2024-04-05', value: 12300 },
    { date: '2024-04-10', value: 12800 },
    { date: '2024-04-15', value: 13200 },
    { date: '2024-04-20', value: 13700 },
    { date: '2024-04-25', value: 14000 },
    { date: '2024-04-30', value: 14250 },
  ],
  engagement: [
    { date: '2024-04-01', value: 62 },
    { date: '2024-04-05', value: 64 },
    { date: '2024-04-10', value: 65 },
    { date: '2024-04-15', value: 67 },
    { date: '2024-04-20', value: 66 },
    { date: '2024-04-25', value: 68 },
    { date: '2024-04-30', value: 68 },
  ]
};

export const MOCK_ELECTIONS: Election[] = [
  {
    id: 'election-1',
    nodeId: 'node-1',
    title: '2024 Chapter Officers Election',
    description: 'Annual election for Chapter President, Secretary, and Treasurer positions. Your voice determines the future of our guild.',
    status: 'ACTIVE',
    startDate: new Date().toISOString(),
    endDate: new Date(Date.now() + 86400000 * 7).toISOString(),
    roles: ['President', 'Secretary', 'Treasurer'],
    allowSelfNomination: true,
    isAnonymous: true
  },
  {
    id: 'election-2',
    nodeId: 'node-1',
    title: 'Constitutional Amendment Vote',
    description: 'Voting on proposed changes to the organizational bylaws regarding membership dues.',
    status: 'UPCOMING',
    startDate: new Date(Date.now() + 86400000 * 14).toISOString(),
    endDate: new Date(Date.now() + 86400000 * 16).toISOString(),
    roles: ['Supporter', 'Opponent'],
    allowSelfNomination: false,
    isAnonymous: true
  }
];

export const MOCK_CANDIDATES: Candidate[] = [
  {
    id: 'cand-1',
    electionId: 'election-1',
    userId: 'u4',
    name: 'Benjamin Franklin',
    role: 'President',
    statement: 'I aim to modernize the chapters communication and foster a stronger relationship with the National society.',
    platform: 'Detailed plan for 2026...',
    votesReceived: 145
  },
  {
    id: 'cand-2',
    electionId: 'election-1',
    userId: 'u5',
    name: 'John Adams',
    role: 'President',
    statement: 'Experience and stability are what we need in these times. I have served as Treasurer for 4 years.',
    platform: 'My track record speaks for itself...',
    votesReceived: 132
  },
  {
    id: 'cand-3',
    electionId: 'election-1',
    userId: 'u6',
    name: 'Thomas Jefferson',
    role: 'Secretary',
    statement: 'Documentation is the soul of any great organization.',
    platform: 'Digital archives for everyone.',
    votesReceived: 88
  }
];

export const MOCK_MEMBER_VOTES: Record<string, string[]> = {
  'me': [] // Tracks which elections the user has voted in
};

export const MOCK_SUBMISSIONS: MemberRequirementSubmission[] = [
  {
    id: 'sub-1',
    requirementId: 'r2',
    requirementName: 'Safe Environment Training',
    memberId: 'user-2',
    memberName: 'Jane Smith',
    evidenceUrl: 'https://example.com/cert.pdf',
    notes: 'Completed the online module yesterday.',
    submittedAt: new Date(Date.now() - 3600000).toISOString()
  }
];

export const MOCK_CHANNELS: any[] = [
  { id: 'ch-1', name: 'General', isPrivate: false, lastMessage: 'See you all tomorrow!' },
  { id: 'ch-2', name: 'Announcements', isPrivate: false, lastMessage: 'Welcome to the new system.' },
  { id: 'ch-3', name: 'Executive-Board', isPrivate: true, lastMessage: 'Budget review scheduled for 5 PM.' }
];

export const MOCK_MESSAGES: Message[] = [
  { 
    id: 'm-1', 
    channelId: 'ch-1', 
    senderUserId: 'user-james', 
    senderName: 'James Avery', 
    senderAvatarColor: 'bg-brand-primary',
    body: 'Hey everyone, check out the new dashboard!', 
    createdAt: new Date().toISOString(),
    isPinned: false
  },
  { 
    id: 'm-2', 
    channelId: 'ch-1', 
    senderUserId: 'user-2', 
    senderName: 'Jane Smith', 
    senderAvatarColor: 'bg-teal-500',
    body: 'Looks great! Love the primary color choice.', 
    createdAt: new Date().toISOString(),
    isPinned: false
  }
];

const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);
tomorrow.setHours(10, 0, 0, 0);

const tomorrowEnd = new Date(tomorrow);
tomorrowEnd.setHours(12, 0, 0, 0);

export const MOCK_EVENTS: OrgEvent[] = [
  {
    id: 'evt-1',
    ownerNodeId: 'node-1',
    ownerNodeName: 'Graffiti HUB',
    title: 'Collaborative Mural Session',
    description: 'Join us for a massive collaborative mural at the downtown location. Bring your own supplies or use ours!',
    eventType: 'WORKSHOP',
    location: 'Downtown Creative Studio',
    startTime: tomorrow.toISOString(),
    endTime: tomorrowEnd.toISOString(),
    allDay: false,
    status: 'SCHEDULED',
    locked: false,
    rsvpCounts: { YES: 12, NO: 2, MAYBE: 5 },
    myRsvp: 'YES'
  },
  {
    id: 'evt-2',
    ownerNodeId: 'node-1',
    ownerNodeName: 'Graffiti HUB',
    title: 'Monthly Community Sync',
    description: 'Reviewing progress, upcoming goals, and voting on the next creative retreat location.',
    eventType: 'MEETING',
    location: 'HUB Discord / Main Hall',
    startTime: new Date(Date.now() + 86400000 * 3).toISOString(),
    endTime: new Date(Date.now() + 86400000 * 3 + 3600000).toISOString(),
    allDay: false,
    status: 'SCHEDULED',
    locked: false,
    rsvpCounts: { YES: 8, NO: 0, MAYBE: 2 },
    myRsvp: 'NONE'
  },
  {
    id: 'evt-3',
    ownerNodeId: 'node-1',
    ownerNodeName: 'Graffiti HUB',
    title: 'Joint Creative Summit',
    description: 'A massive summit bringing together members from Graffiti HUB and Atomic Society to discuss future collaborations.',
    eventType: 'SUMMIT',
    location: 'Central Plaza',
    isJoint: true,
    homeNodeId: 'node-1',
    homeNodeName: 'Graffiti HUB',
    awayNodeId: 'node-6',
    awayNodeName: 'Atomic Society',
    participatingNodeIds: ['node-1', 'node-6'],
    startTime: new Date(Date.now() + 86400000 * 7).toISOString(),
    endTime: new Date(Date.now() + 86400000 * 7 + 7200000).toISOString(),
    allDay: false,
    status: 'SCHEDULED',
    locked: false,
    rsvpCounts: { YES: 45, NO: 5, MAYBE: 12 },
    myRsvp: 'MAYBE'
  },
  {
    id: 'evt-4',
    ownerNodeId: 'node-2',
    ownerNodeName: 'Grand Council',
    title: 'Annual Leadership Retreat',
    description: 'Cascaded event from the Grand Council for all local leaders.',
    eventType: 'RETREAT',
    location: 'Mountain Resort',
    startTime: new Date(Date.now() + 86400000 * 14).toISOString(),
    endTime: new Date(Date.now() + 86400000 * 16).toISOString(),
    allDay: true,
    status: 'SCHEDULED',
    locked: true,
    rsvpCounts: { YES: 150, NO: 10, MAYBE: 20 },
    myRsvp: 'NONE'
  }
];

export const MOCK_ISSUES: Issue[] = [
  {
    id: 'issue-1',
    nodeId: 'node-1',
    originNodeId: 'node-1',
    requesterId: 'user-james',
    authorName: 'James Avery',
    title: 'Cannot access Ledger',
    description: 'I keep getting a permission denied error when trying to view the chapter ledger.',
    category: IssueCategory.TECHNICAL,
    severity: IssueSeverity.MEDIUM,
    status: IssueStatus.OPEN,
    createdAt: new Date(Date.now() - 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 86400000).toISOString()
  },
  {
    // Raised at node-1, escalated up to node-2. node-1 still sees this under "Raised".
    id: 'issue-2',
    nodeId: 'node-2',
    originNodeId: 'node-1',
    requesterId: 'user-2',
    authorName: 'Sarah',
    title: 'Dispute with Chapter Leader',
    description: 'We had a disagreement about the new bylaws and I feel it needs third-party mediation.',
    category: IssueCategory.CONDUCT,
    severity: IssueSeverity.HIGH,
    status: IssueStatus.ESCALATED,
    createdAt: new Date(Date.now() - 172800000).toISOString(),
    updatedAt: new Date(Date.now() - 43200000).toISOString(),
    escalatedToSystem: false,
    escalationTrail: [
      {
        fromNodeId: 'node-1',
        toNodeId: 'node-2',
        escalatedBy: 'user-2',
        escalatedAt: new Date(Date.now() - 43200000).toISOString()
      }
    ],
    updates: [
      {
        authorId: 'user-2',
        message: 'Status changed from OPEN to ESCALATED.',
        timestamp: new Date(Date.now() - 43200000).toISOString()
      }
    ]
  },
  {
    // Escalated all the way to the platform, so it is only actionable by system admins.
    id: 'issue-3',
    nodeId: 'platform-root',
    originNodeId: 'node-1',
    requesterId: 'user-james',
    authorName: 'James Avery',
    title: 'Billing Inquiry',
    description: 'There is a charge on my account that I do not recognize from the national level.',
    category: IssueCategory.BILLING,
    severity: IssueSeverity.LOW,
    status: IssueStatus.ESCALATED,
    createdAt: new Date(Date.now() - 259200000).toISOString(),
    updatedAt: new Date(Date.now() - 86400000).toISOString(),
    escalatedToSystem: true,
    escalationTrail: [
      {
        fromNodeId: 'node-1',
        toNodeId: 'node-2',
        escalatedBy: 'user-james',
        escalatedAt: new Date(Date.now() - 172800000).toISOString()
      },
      {
        fromNodeId: 'node-2',
        toNodeId: 'platform-root',
        escalatedBy: 'user-james',
        escalatedAt: new Date(Date.now() - 86400000).toISOString()
      }
    ],
    updates: []
  }
];

export const MOCK_SPLITS = [
  { nodeId: 'node-1', nodeName: 'Local Chapter', amount: 35.00, nodeColor: '#1A56A0', percentage: 70 },
  { nodeId: 'node-2', nodeName: 'Grand Council', amount: 10.00, nodeColor: '#0F1F3D', percentage: 20 },
  { nodeId: 'platform', nodeName: 'OrgSets Platform', amount: 5.00, nodeColor: '#4285F4', percentage: 10 },
];

export const MOCK_DEPENDENT_ACTIVITY = [
  { id: '1', name: 'Timmy Avery', relation: 'Son', org: 'Springfield Little League', type: 'FEE_DUE', detail: '$25.00 Team Fee', priority: 'HIGH' },
  { id: '2', name: 'Timmy Avery', relation: 'Son', org: 'Springfield Little League', type: 'EVENT', detail: 'Practice @ 5pm Today', priority: 'NORMAL' },
  { id: '3', name: 'Buster', relation: 'Pet', org: 'Golden Retriever Club', type: 'VACCINE', detail: 'Rabies booster due', priority: 'URGENT' },
  { id: '4', name: 'Sarah Avery', relation: 'Spouse', org: 'Downtown Biking Club', type: 'DUES', detail: 'Annual membership renewal', priority: 'NORMAL' },
];

export const MOCK_URGENT_ANNOUNCEMENTS = [
  {
    id: '1',
    title: 'System Maintenance: Saturday 2AM',
    content: `System will be undergoing scheduled maintenance this Saturday. Expect 15 minutes of downtime.`,
    nodeName: `Global`,
  }
];

export const MOCK_UPCOMING_EVENTS = [
  { id: '1', title: 'Quarterly Town Hall', orgName: 'Main Org', date: new Date(Date.now() + 86400000 * 2).toISOString() },
  { id: '2', title: 'Budget Review', orgName: 'Sub Node A', date: new Date(Date.now() + 86400000 * 5).toISOString() },
  { id: '3', title: 'Community Mixer', orgName: 'Main Org', date: new Date(Date.now() + 86400000 * 8).toISOString() },
];

const SEED_ARTICLES = [
  {
    id: "ledger-heritage-societies",
    slug: "3-tier-ledger-heritage-societies",
    date: "May 12, 2026",
    category: "Product Update",
    readTime: "4 min read",
    title: "Introducing the 3-Tier Ledger System for Heritage Societies",
    excerpt: "Automate dues distribution across National, State, and Local nodes with our new cryptographic ledger.",
    content: `## Automating Multi-Tier Dues Distribution

In historically structured organizations, financial management is frequently split between local chapters, regional state societies, and national headquarters. This multi-layered structure often results in significant **Admin Tax**ΓÇöthe hours volunteers spend manually auditing bank accounts, compiling spreadsheets, and distributing split percentages.

### The Pain of Manual Split Ledgers
Traditional ledger systems require local treasurers to collect payments, manually compute state and national portions, write checks, and mail them up the hierarchy. This process is:
1. **Prone to error**: Spreadsheet formulas are easily broken.
2. **Slow**: Funds can take weeks or months to clear and reach the parent node.
3. **Lacking in transparency**: National auditors cannot easily verify local collection records.

### How OrgSets Solves Multi-Tier Dues
OrgSets replaces this friction with our custom **3-Tier Ledger Engine**. When a local member pays their dues on OrgSets:
- **Instant Allocation**: The Stripe Connect gateway splits the transaction at the point of charge.
- **Direct Payout**: 60% of the funds are deposited to the local chapter, 25% to the State Society, and 15% to the National Headquarters automatically.
- **Audit Trails**: Every split is cryptographically linked, creating a single source of truth that is permanently logged in the node's ledger view.

By automating this hierarchy, OrgSets cuts down administrative processing times by over 90%, letting volunteers focus on heritage and community rather than accounting.
`
  },
  {
    id: "sports-admin-chaos",
    slug: "eliminate-youth-sports-admin-chaos",
    date: "May 24, 2026",
    category: "Best Practices",
    readTime: "6 min read",
    title: "Eliminate Administrative Chaos in Youth Sports",
    excerpt: "Ditch the group chats and messy spreadsheets. Learn how to centralize roster placements, waivers, and dues collection in one unified system.",
    content: `## Restoring Order to Youth Sports Operations

For youth sports directors and coaches, the start of every season triggers a storm of administrative overhead. The "Admin Tax" of youth sports includes tracking down physical medical waivers, chasing parents for registration fees, and handling last-minute roster adjustments.

### The Breakdown of Chaotic Channels
Relying on scattered consumer tools introduces security risks and operational friction:
* **WhatsApp and Group Chats**: Crucial logistics get buried in casual chatter.
* **Paper Waivers**: Physical documents are easily lost, creating compliance vulnerabilities.
* **Venmo or Cash**: Chasing outstanding registration fees manually eats up hours of volunteer time.

### Centralized League Management with OrgSets
OrgSets provides an institutional-grade platform built specifically for complex node structures. Here is how we streamline sports operations:

1. **Structured Roster Placements**: Managers can assign roles (coach, player, guardian) directly within sub-nodes (divisions, teams) with custom permissions.
2. **Digital Vault Compliance**: Upload and track safety credentials and liability waivers within our secure **Digital Vault**, notifying parents automatically of missing paperwork.
3. **Automated Registration Fees**: Set up clean payment flows with automated recurring invoices. No more tracking parents down at the field.

Moving your league operations to a unified dashboard reduces overhead, protects participant data, and guarantees that your season starts smoothly.
`
  },
  {
    id: "hoa-admin-tax",
    slug: "true-cost-of-hoa-admin-tax",
    date: "May 18, 2026",
    category: "Governance",
    readTime: "5 min read",
    title: "The True Cost of 'Admin Tax' in HOA Governance",
    excerpt: "Why manual dues collection and scattered text messages drain volunteer board energy, and how structured digital systems restore transparency.",
    content: `## Reclaiming Volunteer Time in Homeowner Associations

Serving on an HOA board is often a thankless task. Board membersΓÇöwho are volunteersΓÇöare frequently burdened with what we call the **Admin Tax**: the heavy operational drag of collecting monthly assessments, responding to maintenance requests, and keeping residents informed.

### The Toll of Administrative Drag
When HOAs operate without dedicated governance software:
- **Dues Collection Frictions**: Volunteers spend weekends writing overdue notices or processing physical checks.
- **Meeting Minutes Dispersal**: Distributing board minutes via email chains leads to resident complaints of low transparency.
- **Security Vetting**: Managing gate codes, vendor contracts, and resident credentials in unsorted spreadsheets risks data breaches.

### The OrgSets Governance Blueprint
OrgSets provides HOA boards with a secure, transparent, and structured environment:
* **Automated Ledgers**: Monthly HOA dues are charged automatically. Residents receive automated receipts, and the board has instant access to real-time financial reporting.
* **Verified Vault Documentation**: Share community guidelines, bylaws, and approved meeting minutes in a centralized vault.
* **Hierarchical Communications**: Broadcast emergency alerts or announcements that cascade down to specific streets or sectors.

By eliminating manual tasks, OrgSets reduces board member burnout and establishes a professional standard of community governance.
`
  },
  {
    id: "soc2-compliance",
    slug: "orgsets-achieves-soc2-compliance",
    date: "April 28, 2026",
    category: "Security",
    readTime: "3 min read",
    title: "OrgSets Security Architecture: Enterprise-Grade AWS Infrastructure & SOC 2 Readiness",
    excerpt: "We're doubling down on our commitment to secure institutional data, cryptographic lineage, and organizational privacy on certified cloud infrastructure.",
    content: `## Institutional-Grade Security for Every Node

As organizations transition their directory structure and ledger systems to the cloud, data security is paramount. OrgSets is engineered from the ground up for enterprise-grade security and continuous SOC 2 Type II readiness.

### Cloud Infrastructure Standards
OrgSets is deployed on AWS cloud infrastructure certified under SOC 2 Type II, ISO 27001, and PCI-DSS Level 1. This ensures that the underlying physical facilities, virtualization, and network security layers meet the highest audited industry standards.

### Our Security Commitments
- **Cryptographic Lineage**: Our hierarchical database architecture ensures that node transitions, role elevations, and ledger splits are verifiable.
- **Encrypted Storage**: Resident, member, and financial files stored in the **Digital Vault** are encrypted in transit with TLS 1.3 and at rest with AES-256.
- **Granular RBAC**: Role-Based Access Control limits access strictly based on dynamic role definitions within each node.

With enterprise-grade encryption and certified cloud infrastructure, OrgSets ensures that your organization's sensitive governance and financial data remains protected.
`
  },
  {
    id: "orgsets-platform-initial-launch",
    slug: "orgsets-platform-initial-launch",
    date: "May 1, 2026",
    category: "Milestone",
    readTime: "3 min read",
    title: "OrgSets Welcomes First Certified Communities",
    excerpt: "Announcing the official public launch of OrgSets and welcoming the first cohort of organizations digitizing their operations.",
    content: `## A Modern Standard for Community Governance

Today, OrgSets officially opened its platform to the public, welcoming our first cohort of certified youth sports leagues, fraternal societies, homeowner associations, and non-profits. 

### Why OrgSets Was Founded
Volunteer-run organizations represent the bedrock of local communities. Yet, most operate using a disjointed patchwork of consumer applications:
* WhatsApp or Facebook groups for communication.
* Excel spreadsheets for member directories.
* Google Drive or Dropbox for storing bylaws.
* Venmo, PayPal, or physical checks for collecting dues.

This fragmentation leads to administrative drag, security vulnerabilities, and board member burnout. OrgSets was built to solve this by consolidating communication, directories, compliance tracking, and automated financial ledgers into a single, secure environment.

### Welcoming Our First Cohort
Over the past several months, a select group of pilot organizations successfully transitioned their operations to OrgSets. Local sports clubs consolidated their roster placements and parent notifications, while homeowner associations digitized their assessment records.

We are excited to expand our onboarding queue and invite community leaders to schedule a demo to see how OrgSets can streamline their operations.`
  },
  {
    id: "celebrating-initial-launch-long",
    slug: "celebrating-our-initial-launch-orgsets-welcomes-first-wave-of-certified-communities",
    date: "May 1, 2026",
    category: "Milestone",
    readTime: "3 min read",
    title: "OrgSets Welcomes First Certified Communities",
    excerpt: "Announcing the official public launch of OrgSets and welcoming the first cohort of organizations digitizing their operations.",
    content: `## A Modern Standard for Community Governance

Today, OrgSets officially opened its platform to the public, welcoming our first cohort of certified youth sports leagues, fraternal societies, homeowner associations, and non-profits. 

### Why OrgSets Was Founded
Volunteer-run organizations represent the bedrock of local communities. Yet, most operate using a disjointed patchwork of consumer applications:
* WhatsApp or Facebook groups for communication.
* Excel spreadsheets for member directories.
* Google Drive or Dropbox for storing bylaws.
* Venmo, PayPal, or physical checks for collecting dues.

This fragmentation leads to administrative drag, security vulnerabilities, and board member burnout. OrgSets was built to solve this by consolidating communication, directories, compliance tracking, and automated financial ledgers into a single, secure environment.

### Welcoming Our First Cohort
Over the past several months, a select group of pilot organizations successfully transitioned their operations to OrgSets. Local sports clubs consolidated their roster placements and parent notifications, while homeowner associations digitized their assessment records.

We are excited to expand our onboarding queue and invite community leaders to schedule a demo to see how OrgSets can streamline their operations.`
  },
  {
    id: "true-cost-youth-sports-admin-tax",
    slug: "true-cost-youth-sports-admin-tax",
    date: "May 24, 2026",
    category: "Best Practices",
    readTime: "5 min read",
    title: "The True Cost of 'Admin Tax' in Youth Sports",
    excerpt: "Ditch the fragmented app stack. Learn how consolidating rosters, digital waivers, and registration fees into one system saves league directors hours each week.",
    content: `## Eliminating the Operational Drag of League Management

Every season, youth sports directors and division commissioners face a massive challenge. The time spent tracking down medical waivers, manually sorting players into rosters, and chasing parents for outstanding registration fees represents a heavy toll we call the **Admin Tax**.

### The Problem with Fragmented App Stacks
When a league operates without a centralized platform, coordination is usually split across multiple tools:
1. **WhatsApp/SMS**: Critical schedule updates get lost in casual chatter.
2. **Paper Waivers**: Physical documents are easily misplaced, creating compliance vulnerabilities.
3. **Venmo/Cash**: Chasing payments manually and matching receipts to player lists takes hours of volunteer time.

### The Power of Consolidation
OrgSets replaces this chaotic setup with a single, unified league hub:
* **Consolidated Tools**: Roster management, communication, digital waiver signing, and fee collection live in one secure place.
* **Role-Based Directories**: Assign roles (coach, player, guardian) automatically, keeping emergency contacts linked and securely restricted.
* **Automated Payments**: Charge registration fees and process splits (e.g., league insurance vs. team fees) in a single transaction.

Consolidating these operations reduces overhead, protects participant data, and lets volunteers focus on player development rather than administrative paperwork.`
  },
  {
    id: "hoa-governance-volunteer-burnout",
    slug: "hoa-governance-volunteer-burnout",
    date: "May 18, 2026",
    category: "Governance",
    readTime: "5 min read",
    title: "HOA Board Burnout: Reclaiming Volunteer Time",
    excerpt: "How automated assessment ledgers and transparent audit trails protect association boards, prevent dues embezzlement, and save volunteers hours.",
    content: `## Professionalizing Community Management Without the Overhead

Serving on a Homeowner Association (HOA) board is a volunteer position that frequently turns into a second job. Board members are tasked with collecting monthly assessments, reviewing architectural requests, and keeping residents informedΓÇötasks that are difficult to manage without proper tools.

### Transparency and Preventing Financial Mismanagement
Collecting HOA dues manually using paper checks or spreadsheets is not only slow; it also introduces security risks. Without transparent financial records, boards are vulnerable to tracking errors, disputes, and in worst-case scenarios, dues embezzlement.

### The OrgSets HOA Governance Blueprint
OrgSets provides HOA boards with a secure, transparent environment designed to professionalize community operations:
1. **Automated Assessment Ledgers**: Residents enroll in automated recurring payments. All payments are recorded in a real-time, tamper-evident ledger.
2. **Centralized Vault Archives**: Store bylaws, covenants, and approved meeting minutes in a secure public-facing folder, providing residents with instant access to community documentation.
3. **Audit Trails**: Every ledger entry is permanent, ensuring complete financial transparency and protecting the board from liability.

By automating dues collection and establishing clear, structured communication, boards can reduce volunteer burnout and build trust within the community.`
  },
  {
    id: "secure-identity-clerk-integration",
    slug: "secure-identity-clerk-integration",
    date: "May 15, 2026",
    category: "Security",
    readTime: "4 min read",
    title: "Seamless Identity Access via Clerk Integration",
    excerpt: "Learn how our integration with Clerk secures user accounts, enforces multi-factor authentication, and eliminates password management liability.",
    content: `## Secure Authentication for Community Platforms

Data breaches are a growing concern for online platforms. When organizations collect member addresses, phone numbers, and payment details, securing this data is a top priority.

### The Risk of Local Password Storage
Legacy platforms that store usernames and passwords locally in their own databases are prime targets for attacks. If a database is compromised, user credentials are leaked, putting members at risk.

### Advanced Security with Clerk
OrgSets partners with **Clerk**, a leader in identity management, to handle authentication securely:
* **No Local Password Storage**: User credentials are managed by Clerk's secure servers. OrgSets only handles verified session tokens.
* **Seamless Multi-Factor Authentication (MFA)**: Members can easily enable SMS or authenticator app codes to protect their accounts.
* **Single Sign-On (SSO)**: Members can log in securely using their existing Google or Apple accounts.

By outsourcing authentication to a secure provider, OrgSets protects member data and ensures your organization meets modern security standards.`
  },
  {
    id: "union-compliance-audit-trails",
    slug: "union-compliance-audit-trails",
    date: "May 10, 2026",
    category: "Governance",
    readTime: "6 min read",
    title: "Simplifying Audits in Multi-Level Trade Unions",
    excerpt: "A guide on utilizing stateless digital ledgers and granular RBAC to streamline compliance and financial tracking in labor organizations.",
    content: `## Streamlining Labor Compliance Across Local and Regional Tiers

Modern trade unions face a complex regulatory environment requiring strict financial reporting, transparent voting procedures, and accurate membership lists across multiple tiers.

### The Admin Tax of Union Bookkeeping
Relying on legacy accounting software and local spreadsheets to track dues and compliance files leads to significant administrative drag.
* **Dues Reconciliation**: Manually tracking local chapter contributions, regional funds, and national assets takes weeks.
* **Credential Tracking**: Verification of safety certifications, licenses, and training requirements is often scattered and hard to audit.
* **Access Control**: Administrative rights are frequently shared, leading to security risks.

### The OrgSets Union Governance Blueprint
OrgSets helps unions modernize their administrative infrastructure:
1. **Dynamic Payout Ledgers**: Automate split dues automatically. When a member pays their monthly dues, the platform routes local, regional, and national percentages instantly.
2. **Granular RBAC**: Assign roles based on dynamic capabilities, ensuring local officers only access their local registry while national auditors have read-only access across all nodes.
3. **Compliance Tracking**: Store safety credentials and union cards within the secure Digital Vault, notifying members automatically before their certifications expire.

Modernizing union management with digital ledgers reduces administrative errors and ensures compliance at every tier.`
  },
  {
    id: "introducing-3-tier-dynamic-payout-ledger",
    slug: "introducing-3-tier-dynamic-payout-ledger",
    date: "May 8, 2026",
    category: "Product Update",
    readTime: "4 min read",
    title: "Introducing the 3-Tier Dynamic Payout Ledger",
    excerpt: "A technical overview of our automated fee splitting and instant ledger allocation across hierarchical organization structures.",
    content: `## Automating Dues Distribution Across Multi-Level Hierarchies

Historically structured organizations, such as fraternal orders, heritage societies, and national associations, often have complex financial distributions. Dues collected locally must be split and sent up the hierarchy. 

### The Cost of Manual Splits
Traditional processes involve local treasurers collecting funds, manually calculating percentages, and mailing physical checks to regional and national offices. This manual process results in delay, accounting errors, and a lack of transparency.

### The OrgSets Split Engine
OrgSets solves this with our automated **3-Tier Ledger Engine**, which automatically routes funds at the point of transaction:
* **Stripe Connect Integration**: Splits are calculated and processed during checkout.
* **Instant Distribution**: Funds are routed directly to the bank accounts of the local, regional, and national nodes.
* **Cryptographic Records**: Every transaction is logged with a secure hash, creating an immutable audit trail.

This automation cuts administrative processing times by over 90%, letting volunteers focus on their mission rather than manual bookkeeping.`
  },
  {
    id: "non-profit-volunteer-burnout-prevention",
    slug: "non-profit-volunteer-burnout-prevention",
    date: "May 5, 2026",
    category: "Best Practices",
    readTime: "5 min read",
    title: "Managing Volunteer Burnout in Non-Profits",
    excerpt: "Learn how consolidating file storage, communication channels, and membership rosters into a single hub keeps volunteers engaged.",
    content: `## Reclaiming Mission-Focus by Eliminating Operational Drag

Non-profit organizations run on volunteer energy. However, volunteers often find themselves bogged down by the "Admin Tax" of running the organizationΓÇösorting spreadsheets, tracking meeting minutes, and chasing donors.

### The Cost of Volunteer Fatigue
When administrative tasks become overwhelming, volunteer retention drops, slowing down key initiatives.
* **Scattered Files**: Bylaws, grant reports, and meeting minutes are often stored in personal email threads or drive folders, leading to lost documentation.
* **Unstructured Communication**: Using email lists or chat apps leads to critical announcements being missed.
* **Manual Roster Management**: Manually updating member spreadsheets takes hours.

### Simplifying Administration with OrgSets
OrgSets helps non-profits streamline their administration:
1. **Centralized Vault**: Keep all organizational documents, bylaws, and grant files in a secure, shared vault.
2. **Hierarchical Broadcasts**: Send critical announcements that automatically cascade down to the relevant committees or chapters.
3. **Structured Member Directories**: Manage your directory with defined roles, making roster updates easy.

Reducing administrative drag helps keep volunteers engaged and focused on the non-profit's mission.`
  },
  {
    id: "election-guard-cryptographic-voting",
    slug: "election-guard-cryptographic-voting",
    date: "May 3, 2026",
    category: "Product Update",
    readTime: "5 min read",
    title: "Election Guard: Secure Digital Voting",
    excerpt: "Announcing our cryptographic, tamper-evident voting system for secure board elections and governance.",
    content: `## Ensuring Trust and Integrity in Board Elections

Transparent voting is critical to effective organizational governance. Traditional voting methods (paper ballots or simple online forms) are often slow, hard to verify, and vulnerable to errors.

### The Challenges of Online Voting
Most simple online voting tools lack proper access controls, allowing duplicate votes or voting by ineligible members, which can compromise election integrity.

### Secure Voting with Election Guard
OrgSets is proud to introduce **Election Guard**, our cryptographic voting engine designed for secure board elections:
* **One-Vote-Per-Member Verification**: Integrates directly with your verified roster to ensure only eligible members can vote once.
* **Anonymized Ballots**: Voter identities are decoupled from their ballots to ensure complete privacy.
* **Tamper-Evident Logs**: Every cast vote is hashed and logged, creating a verifiable audit trail that is easy to audit.

Election Guard provides organizations with the tools to conduct secure, auditable, and transparent elections with ease.`
  },
  {
    id: "custom-domains-dns-organizational-trust",
    slug: "custom-domains-dns-organizational-trust",
    date: "April 30, 2026",
    category: "Best Practices",
    readTime: "4 min read",
    title: "The Importance of Custom Domains and Verified DNS",
    excerpt: "Why custom domains and verified DNS are essential for building brand authority, ensuring email deliverability, and protecting user security.",
    content: `## Building Trust with Custom Brand Infrastructure

For modern organizations, branding consistency is key to building trust. When communicating with members, residents, or donors, sending emails or hosting portals on generic subdomains can compromise brand authority.

### The Risks of Generic Subdomains
Relying on generic domain structures can lead to issues with brand recognition and email deliverability.
* **Phishing Vulnerabilities**: Members may find it difficult to distinguish between official portals and phishing attempts.
* **Email Spam Filters**: Transactional emails sent from generic domains are more likely to be flagged as spam.
* **Inconsistent Branding**: Using generic links dilutes your brand's presence.

### Professional Branding with OrgSets
OrgSets makes it easy for organizations to set up custom branding:
1. **Vanity Domain Routing**: Connect your custom domain (e.g., \`portal.yourassociation.org\`) directly to your OrgSets node.
2. **Verified DNS Setup**: Simple DNS configurations (SPF, DKIM) ensure high email deliverability.
3. **SSL Certificates**: All connected custom domains are automatically secured with SSL certificates.

Setting up custom domains and verified DNS helps protect your brand's security and ensures consistent communication.`
  },
  {
    id: "secure-credential-management-vault",
    slug: "secure-credential-management-vault",
    date: "April 29, 2026",
    category: "Product Update",
    readTime: "5 min read",
    title: "Secure Credential Management Vault",
    excerpt: "Learn how the Digital Vault tracks safety credentials, liability waivers, and background checks with automated renewal warnings.",
    content: `## Maintaining Compliance and Safety Standards with Ease

For youth leagues, schools, and non-profits, volunteer safety compliance is non-negotiable. Tracking background checks, liability waivers, and certification cards is a heavy administrative burden that must be managed carefully.

### The Danger of Compliance Overlook
Relying on physical binders or unsorted folders makes it easy to overlook expired certifications:
* **Liability Risks**: If a volunteer's background check expires unnoticed, the organization faces legal exposure.
* **Audit Pressures**: Finding and verifying individual safety credentials during audits can take hours.
* **Sensitive Data Exposure**: Keeping copies of driver's licenses and background checks in local spreadsheets poses privacy risks.

### Streamlining Compliance with OrgSets
OrgSets helps organizations manage safety compliance securely:
1. **Granular Vault Access**: Members upload safety credentials directly to their profile vault, which is only accessible to verified compliance administrators.
2. **Automated Renewal Warnings**: The system tracks expiration dates and sends automated reminders before credentials expire.
3. **Direct Auditing**: Compliance officers can run quick directory reports to verify that all active coaches or volunteers meet required standards.

Centralizing safety credentials in a secure, digital vault helps protect your organization and keep your community safe.`
  },
  {
    id: "behind-the-brand-orgsets-domain-journey",
    slug: "behind-the-brand-orgsets-domain-journey",
    date: "April 25, 2026",
    category: "Governance",
    readTime: "5 min read",
    title: "The Evolution and Alignment of OrgSets",
    excerpt: "How our product experiments across OrgWrangler, Loop Collab, and OrgTome helped us refine our target audiences and led to OrgSets as our unified backend engine.",
    content: `## Finding the Definitive Engine for Modern Organizations

If you have followed our development journey, you may have encountered several different names and domains connected to our early product releases, including **OrgWrangler.com**, **Loop-Collab.com**, **OrgTome.com**, and **OrgSets.com**. 

Far from being random names, each represents a distinct chapter in our product evolution and our effort to identify the specific operational needs of B2B community networks. Here is how we defined each segmentΓÇöand why OrgSets became the unified winner for our platform architecture.

### The Four Domain Experiments

#### 1. OrgWrangler.com (Target: Local Operations & Volunteers)
The initial concept focused heavily on the physical "wrangling" of local organization overhead. The target audience was small, volunteer-run operations like local neighborhood clubs or community gardens. The goal was simple: replace spreadsheet messiness with automated tools. However, the term "wrangling" felt too informal for larger, institutional networks that required rigorous corporate structure and compliance.

#### 2. Loop-Collab.com (Target: Real-Time Chat & Collaboration)
This experiment focused on communication. The goal was to replace chaotic group chats (like WhatsApp or Slack) with structured thread loops. While collaborative chat is crucial, we realized that messaging alone is not a sufficient solution. A communications tool without an underlying directory registry, roles, and automated ledgers is just another inbox.

#### 3. OrgTome.com (Target: Archival Vaults & Registries)
The "Tome" concept focused on documentation, vaults, bylaws, and compliance archives. It targeted fraternal orders, heritage organizations, and trade unions that required strict record retention. Although the archive vault became a core feature, a static repository lacked the dynamic interaction required for daily operational billing and event coordination.

#### 4. OrgSets.com (The Unified Winner)
In mathematics, a **set** is a collection of distinct, well-defined elements. **OrgSets** represents this exact mathematical structure applied to organizations: a unified set of members, roles, sub-nodes, digital vaults, and split-ledgers. 

Instead of forcing users to manage separate apps, OrgSets combines:
* The operational tools of *OrgWrangler*
* The real-time threads of *Loop Collab*
* The document compliance vaults of *OrgTome*

By unifying these components, OrgSets provides a robust, multi-tenant B2B SaaS architecture. It represents the definitive hierarchical engine for our internal engineering, databases, and api backend. All sub-domains and experimental features are now integrated under this unified brand to deliver "Governance at Scale."`
  },
  {
    id: "senior-living-social-hub",
    slug: "senior-living-social-hub",
    date: "April 20, 2026",
    category: "Best Practices",
    readTime: "5 min read",
    title: "Transforming Senior Living into Social Hubs",
    excerpt: "Discover how modernizing activity calendars, social directories, and facility bookings can dramatically improve resident engagement in senior living communities.",
    content: `## Modernizing Connection in Retirement Communities

For senior living and 55+ communities, resident well-being is deeply tied to social engagement. Yet, many facilities still rely on outdated methods to keep residents connectedΓÇösuch as physical bulletin boards, printed newsletters, and fragmented email lists.

### The Problem with Analog Coordination
When a community relies on analog tools, it creates unnecessary barriers to connection:
* **Missed Opportunities**: Printed calendars are easily misplaced or ignored, causing residents to miss out on events like Thursday night Bingo or group outings.
* **Isolated Social Scenes**: Without an easy way to discover neighbors with shared interests (e.g., Canasta players or morning walkers), new residents can struggle to build a social circle.
* **Inefficient Facility Booking**: Managing reservations for the community clubhouse or shuffleboard courts on a paper sign-up sheet leads to double-bookings and frustration.

### The OrgSets Senior Living Solution
OrgSets transforms how senior living communities operate by providing an accessible, unified digital hub:
1. **Interactive Activity Calendar**: Residents can easily browse upcoming events, RSVP for activities, and see who else is attending.
2. **Opt-in Social Directory**: A secure, resident-only phonebook where individuals can list their interests, making it simple to find a chess partner or organize a walking group.
3. **High-Contrast Communication**: The platform's customizable themes support high-contrast, accessible designs, ensuring daily announcements and newsletters are easy for everyone to read.

By digitizing community management, facility directors can save hours of administrative work while giving residents the tools they need to build a vibrant, active social life.`
  }
];

const articlesMap = new Map<string, any>();
SEED_ARTICLES.forEach((art: any) => {
  if (art?.slug) articlesMap.set(art.slug, art);
});
(preloadedArticles as any[]).forEach((art: any) => {
  if (art?.slug) articlesMap.set(art.slug, { ...(articlesMap.get(art.slug) || {}), ...art });
});

export const MOCK_ARTICLES = Array.from(articlesMap.values());

export const MOCK_REFERRALS: Referral[] = [
  {
    id: 'ref-1',
    referrerNodeId: 'node-1',
    referredNodeName: 'Sons of the American Revolution - Chapter B',
    status: 'ACCEPTED',
    referralCode: 'FOUNDER-2024',
    creditsEarned: 5000,
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    acceptedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: 'ref-2',
    referrerNodeId: 'node-1',
    referredNodeName: 'Founding Daughters Association',
    status: 'PENDING',
    referralCode: 'FOUNDER-2024',
    creditsEarned: 0,
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
  }
];
